import React, { useEffect, useMemo, useState } from 'react';
import {
  getVehicleIdFromUrl,
  Warranty,
  useDesktopOrMobileView,
  CompleteVehicleEntry,
} from '@oneaudi/vtp-shared';
import WarrantyComponent from './Warranty';
import { Headline, Layout, LayoutItem } from '@oneaudi/unified-web-components';
import { useI18n } from '@oneaudi/i18n-context';
import styled from 'styled-components';
import {
  SYS_BREAKPOINT_LG,
  SYS_BREAKPOINT_MD,
  SYS_BREAKPOINT_XL,
  SYS_SPACE_FIXED_200,
  SYS_SPACE_FIXED_300,
  SYS_SPACE_FIXED_450,
} from '@oneaudi/unified-web-common';
import { WarrantiesPagination } from './WarrantiesPagination';

export interface WarrantiesProps {
  vehicle: CompleteVehicleEntry;
}

const Warranties: React.FC<WarrantiesProps> = ({ vehicle }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [currentWarrantyItems, setCurrentWarrantyItems] = useState<
    {
      warranty: Warranty;
      component: React.JSX.Element;
    }[]
  >([]);

  const vehicleID = vehicle.basic.id || getVehicleIdFromUrl();
  const headline = useI18n({ id: 'nemo.ui.sc.warranties.headline', defaultMessage: 'Warranties' });

  if (
    !vehicle?.basic.warrantyInfo?.warranties ||
    vehicle.basic.warrantyInfo.warranties.length === 0
  ) {
    return null;
  }

  // sort in JS mutates the array and `warranties` is not mutable
  const warranties: Warranty[] = [...vehicle.basic.warrantyInfo.warranties].sort((a, b) =>
    a.order > b.order ? 1 : -1
  );

  const warrantyItems = warranties.map((warranty) => {
    return {
      warranty,
      component: (
        <WarrantyComponent
          pos={Number(vehicleID) + 1}
          warranty={warranty}
          vehicleType={vehicle.basic.type === 'U' ? 'used' : 'new'}
        />
      ),
    };
  });

  const isAtLeastMBreakpoint = useDesktopOrMobileView(SYS_BREAKPOINT_MD) === 'desktop';
  const isAtLeastLBreakpoint = useDesktopOrMobileView(SYS_BREAKPOINT_LG) === 'desktop';
  const isAtLeastXLBreakpoint = useDesktopOrMobileView(SYS_BREAKPOINT_XL) === 'desktop';

  const itemsPerView = useMemo(() => {
    if (!isAtLeastMBreakpoint) {
      return 1;
    }
    if (!isAtLeastLBreakpoint) {
      return 2;
    }
    if (!isAtLeastXLBreakpoint) {
      return 3;
    }
    return 4;
  }, [isAtLeastMBreakpoint, isAtLeastLBreakpoint, isAtLeastXLBreakpoint]);

  const warrantiesCount = warranties.length;

  const numberOfPagesForWarranties = useMemo(() => {
    return Math.ceil(warrantiesCount / itemsPerView);
  }, [warrantiesCount, itemsPerView]);

  const handlePageChange = (
    eventName: 'prev' | 'next',
    setPage: React.Dispatch<React.SetStateAction<number>>,
    maxPage: number
  ) => {
    setPage((prev) => {
      if (eventName === 'prev') return Math.max(prev - 1, 0);
      if (eventName === 'next') return Math.min(prev + 1, maxPage - 1);
      return prev;
    });
  };

  const onWarrantyPageChange = (eventName: 'prev' | 'next') => {
    handlePageChange(eventName, setCurrentPage, numberOfPagesForWarranties);
  };

  useEffect(() => {
    setCurrentPage(0);
  }, [itemsPerView]);

  useEffect(() => {
    const currentWarrantyItemsTemp = warrantyItems.splice(currentPage * itemsPerView, itemsPerView);
    setCurrentWarrantyItems(currentWarrantyItemsTemp);
  }, [itemsPerView, currentPage]);

  return (
    <Layout
      data-testid="warranty-section"
      direction="column"
      wrap="nowrap"
      margin={[0, 0, SYS_SPACE_FIXED_450, 0]}
    >
      <Headline
        data-testid="headline"
        variant={{
          order: '3',
          type: 'headline',
          weight: 'normal',
        }}
        tag="h2"
        margin={[SYS_SPACE_FIXED_300, 0, SYS_SPACE_FIXED_300, 0]}
      >
        {headline}
      </Headline>
      <Layout
        data-testid="warranties"
        direction="column"
        wrap="nowrap"
        padding={[SYS_SPACE_FIXED_200, 0, SYS_SPACE_FIXED_200, 0]}
      >
        <WarrantiesContainer data-testid="warranties-container">
          {currentWarrantyItems.map((warrantyItem) => {
            return <StyledLayoutItem key={vehicleID}>{warrantyItem.component}</StyledLayoutItem>;
          })}
        </WarrantiesContainer>
        <WarrantiesPagination
          totalPages={numberOfPagesForWarranties}
          current={currentPage}
          onPageChange={onWarrantyPageChange}
          data-testid="warranties-pagination"
        />
      </Layout>
    </Layout>
  );
};

const WarrantiesContainer = styled.div`
  display: grid;
  gap: ${SYS_SPACE_FIXED_300};
  margin-bottom: ${SYS_SPACE_FIXED_300};
  @media (min-width: ${SYS_BREAKPOINT_MD}) and (max-width: calc(${SYS_BREAKPOINT_LG} - 1px)) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: ${SYS_BREAKPOINT_LG}) and (max-width: calc(${SYS_BREAKPOINT_XL} - 1px)) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (min-width: ${SYS_BREAKPOINT_XL}) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StyledLayoutItem = styled(LayoutItem)`
  height: 100%;
`;

export default Warranties;
